/**
 * Overview — Phase 2 Today rollup landing page.
 *
 * Reads GET /api/v1/admin/overview/today and renders 4 stat cards, a
 * "Needs attention" deep-link list, a Today-by-source bar chart, weekly
 * lead total + sparkline, and a Quick Actions sidebar.
 *
 * Endpoint shape (see backend/server.py admin_overview_today):
 * { today, needsAttention[], bySource[], notificationCounts, generated_at }
 */
import { useEffect, useId, useState } from 'react';
import {
  Users,
  Calendar,
  Video,
  CreditCard,
  Plus,
  Download,
  Sparkles,
  RefreshCw,
  Zap,
  Target,
  ChevronRight,
  ArrowUp,
  ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';
import StatusBoard from './StatusBoard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_COLOR = {
  contact: '#11a0c2',
  holiday: '#f43f5e',
  letstalktaxes: '#7c5cff',
  referral: '#10b981',
  coffee: '#b8742a',
  ppc: '#f59e0b',
  ltt_kgob: '#0c7e9b',
};

const TONE_BG = {
  warn: 'rgba(245, 158, 11, 0.16)',
  bad: 'rgba(239, 68, 68, 0.16)',
  info: 'rgba(17, 160, 194, 0.16)',
};
const TONE_FG = {
  warn: '#f59e0b',
  bad: '#ef4444',
  info: '#11a0c2',
};


function agoLabel(sec) {
  if (sec == null) return 'never';
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  return `${Math.floor(sec / 3600)}h ago`;
}

function SyncHealthCard({ sessionToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!sessionToken) return;
    try {
      const r = await fetch(`${BACKEND_URL}/api/v1/admin/sync-health`, { headers: { Authorization: `Bearer ${sessionToken}` } });
      if (r.ok) setData(await r.json());
    } catch (e) { /* non-fatal */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); /* eslint-disable-next-line */ }, [sessionToken]);

  const inSync = data?.in_sync;
  const svc = data?.services || {};
  const Row = ({ name, info }) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 0' }} data-testid={`sync-row-${name}`}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 8, height: 8, borderRadius: '50%', background: info?.connected && info?.healthy ? '#10b981' : info?.connected ? '#f59e0b' : '#ef4444', boxShadow: info?.connected && info?.healthy ? '0 0 8px #10b98188' : 'none' }} />
        <span style={{ fontSize: 13, color: 'var(--text)' }}>{name}</span>
      </div>
      <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{info?.connected ? `seen ${agoLabel(info.age_seconds)}` : 'offline'}</span>
    </div>
  );

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }} data-testid="sync-health-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>CRM Integration</div>
        {!loading && (
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999,
            background: inSync ? 'rgba(16,185,129,0.16)' : 'rgba(239,68,68,0.16)',
            color: inSync ? '#10b981' : '#ef4444',
          }} data-testid="sync-health-badge">
            {inSync ? 'Connected & In Sync' : 'Sync Issue'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-dim)', marginBottom: 8 }}>kgob.com ↔ app.kgob.com via shared bridge</div>
      {loading ? (
        <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>Checking…</div>
      ) : (
        <>
          <Row name="kgob.com" info={svc['kgob.com']} />
          <Row name="app.kgob.com" info={svc['app.kgob.com']} />
          <Row name="Shared bridge" info={{ connected: data?.bridge_connected, healthy: data?.bridge_connected }} />
        </>
      )}
    </div>
  );
}

function StatCard({ Icon, value, label, delta, deltaDir }) {
  return (
    <div className="lb2-card lb2-card-pad" style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-md)', padding: 18,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 'var(--r-md)',
        display: 'grid', placeItems: 'center',
        background: 'var(--info-soft)', color: 'var(--accent-deep)',
        marginBottom: 12,
      }}>
        <Icon size={19} />
      </div>
      <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', color: 'var(--text)' }}>{value}</div>
      <div style={{ fontSize: 12.5, color: 'var(--muted)', fontWeight: 700, marginTop: 2 }}>{label}</div>
      {delta != null && (
        <span style={{
          marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
          fontSize: 11.5, fontWeight: 700,
          color: deltaDir === 'up' ? 'var(--ok)' : deltaDir === 'down' ? 'var(--bad)' : 'var(--muted)',
        }}>
          {deltaDir === 'up' && <ArrowUp size={11} />}
          {deltaDir === 'down' && <ArrowDown size={11} />}
          {delta}
        </span>
      )}
    </div>
  );
}

function Spark({ data, w = 130, h = 56, color = '#11a0c2' }) {
  const id = useId();
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  const min = Math.min(...data, 0);
  const pts = (data || []).map((v, i) => {
    const x = (i / (data.length - 1 || 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * (h - 6) - 3;
    return [x, y];
  });
  const d = (pts || []).map((p, i) => (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1)).join(' ');
  const area = d + ` L${w} ${h} L0 ${h} Z`;
  const gradId = `sg${id.replace(/:/g, '')}`;
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} style={{ display: 'block' }}>
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradId})`} />
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={color} />
    </svg>
  );
}

function Bar({ pct, color = '#11a0c2' }) {
  return (
    <div style={{ height: 8, borderRadius: 99, background: 'var(--surface-3)', overflow: 'hidden' }}>
      <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: color, borderRadius: 99 }} />
    </div>
  );
}

export default function Overview({ onNav, user, sessionToken }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      setLoading(true);
      setErr('');
      try {
        const r = await fetch(`${BACKEND_URL}/api/v1/admin/overview/today`, {
          headers: { Authorization: `Bearer ${sessionToken}` },
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const json = await r.json();
        if (alive) setData(json);
      } catch (e) {
        if (alive) setErr(String(e.message || e));
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    return () => { alive = false; };
  }, [sessionToken]);

  const today = data?.today || {};
  const needs = data?.needsAttention || [];
  const sources = data?.bySource || [];
  const maxSrc = Math.max(...sources.map((s) => s.n), 1);
  const firstName = (user?.name || user?.email || 'there').split(' ')[0];
  const todayStr = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  const greeting = (() => {
    const h = new Date().getHours();
    if (h >= 5 && h < 12) return 'Good morning';
    if (h >= 12 && h < 17) return 'Good afternoon';
    if (h >= 17 && h < 21) return 'Good evening';
    return 'Working late';
  })();

  return (
    <div data-testid="tab-overview" style={{ fontFamily: 'Figtree, system-ui, sans-serif', color: 'var(--text)' }}>
      {/* Page head */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 4px', color: 'var(--text)' }} data-testid="page-title">
            Good morning, {firstName}
          </h1>
          <p style={{ margin: 0, color: 'var(--muted)', fontSize: 14 }}>{todayStr} · All times ET</p>
        </div>
      </div>

      {/* Loading + error states */}
      {loading && (
        <div data-testid="overview-loading" className="lb2-overview-skeleton">
          {/* 4 stat-card skeletons — full layout (icon + value + label + delta) */}
          <div className="lb2-skel-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 18, marginBottom: 18 }}>
            {[1, 2, 3, 4].map((i) => (
              <div key={i} style={{
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-md)', padding: 18,
              }}>
                <div className="lb2-shimmer" style={{ width: 38, height: 38, borderRadius: 'var(--r-md)', marginBottom: 12 }} />
                <div className="lb2-shimmer" style={{ width: '50%', height: 24, borderRadius: 6, marginBottom: 8 }} />
                <div className="lb2-shimmer" style={{ width: '70%', height: 12, borderRadius: 4 }} />
              </div>
            ))}
          </div>

          {/* Two-column skeleton matching the loaded layout */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 18 }} className="lb2-cols-main">
            <div style={{ display: 'grid', gap: 18 }}>
              {/* Needs attention skeleton */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div className="lb2-shimmer" style={{ width: 28, height: 28, borderRadius: 8 }} />
                  <div className="lb2-shimmer" style={{ width: 160, height: 14, borderRadius: 4 }} />
                </div>
                {[1, 2].map((i) => (
                  <div key={i} className="lb2-shimmer" style={{ height: 44, borderRadius: 'var(--r-md)', marginBottom: 10 }} />
                ))}
              </div>
              {/* By source skeleton */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div className="lb2-shimmer" style={{ width: 28, height: 28, borderRadius: 8 }} />
                  <div className="lb2-shimmer" style={{ width: 200, height: 14, borderRadius: 4 }} />
                </div>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                    <div className="lb2-shimmer" style={{ width: 110, height: 22, borderRadius: 99, flex: '0 0 110px' }} />
                    <div className="lb2-shimmer" style={{ flex: 1, height: 8, borderRadius: 99 }} />
                    <div className="lb2-shimmer" style={{ width: 24, height: 12, borderRadius: 4 }} />
                  </div>
                ))}
              </div>
            </div>
            <div style={{ display: 'grid', gap: 18, alignContent: 'start' }}>
              {/* Status board skeleton */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <div className="lb2-shimmer" style={{ width: 28, height: 28, borderRadius: 8 }} />
                  <div className="lb2-shimmer" style={{ width: 110, height: 14, borderRadius: 4 }} />
                </div>
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="lb2-shimmer" style={{ height: 36, borderRadius: 'var(--r-sm)', marginBottom: 8 }} />
                ))}
              </div>
              {/* Weekly leads skeleton */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div className="lb2-shimmer" style={{ width: 120, height: 12, borderRadius: 4, marginBottom: 8 }} />
                <div className="lb2-shimmer" style={{ width: 64, height: 28, borderRadius: 6 }} />
              </div>
              {/* Quick actions skeleton */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div className="lb2-shimmer" style={{ width: 110, height: 14, borderRadius: 4, marginBottom: 14 }} />
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="lb2-shimmer" style={{ height: 36, borderRadius: 'var(--r-sm)', marginBottom: 8 }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {err && !loading && (
        <div data-testid="overview-error" style={{ padding: 16, borderRadius: 'var(--r-md)', background: 'var(--bad-soft)', color: 'var(--bad)', marginBottom: 16, fontWeight: 600, fontSize: 13 }}>
          Couldn&apos;t load the rollup ({err}). <button data-testid="overview-button-1" onClick={() => window.location.reload()} style={{ marginLeft: 8, textDecoration: 'underline', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>Retry</button>
        </div>
      )}

      {/* Stat cards */}
      {!loading && !err && (
        <>
          <div data-testid="overview-stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 18, marginBottom: 18 }}>
            <StatCard Icon={Users} value={today.newLeads ?? 0} label="New leads today" delta={today.weekDeltaPct ? `${today.weekDeltaPct > 0 ? '+' : ''}${today.weekDeltaPct}% wk` : null} deltaDir={today.weekDeltaPct > 0 ? 'up' : today.weekDeltaPct < 0 ? 'down' : 'flat'} />
            <StatCard Icon={Calendar} value={today.bookings ?? 0} label="Bookings today" />
            <StatCard Icon={Video} value={today.consultations ?? 0} label="Consultations" />
            <StatCard Icon={CreditCard} value={`$${(today.revenue ?? 0).toLocaleString()}`} label="Revenue today" />
          </div>

          {/* Two-column grid: left (Needs Attention + By Source) / right (Weekly + Quick Actions) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) 340px', gap: 18 }} className="lb2-cols-main">
            <div style={{ display: 'grid', gap: 18 }}>
              {/* Needs attention */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--info-soft)', color: 'var(--accent-deep)', display: 'grid', placeItems: 'center' }}><Zap size={15} /></span>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>Needs your attention</h2>
                </div>
                {needs.length === 0 ? (
                  <div data-testid="overview-empty-attention" style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>All caught up — nothing pending.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 10 }}>
                    {needs.map((n) => (
                      <button
                        key={`${n.tab}-${n.label}`}
                        data-testid="overview-attention-item"
                        onClick={() => onNav(n.tab)}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 12,
                          padding: '12px 14px', borderRadius: 'var(--r-md)',
                          border: '1px solid var(--border)', background: 'var(--surface-2)',
                          textAlign: 'left', width: '100%', cursor: 'pointer',
                          fontFamily: 'inherit', color: 'inherit',
                        }}
                      >
                        <span style={{
                          minWidth: 38, height: 28, padding: '0 10px',
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          borderRadius: 99, background: TONE_BG[n.tone] || TONE_BG.info,
                          color: TONE_FG[n.tone] || TONE_FG.info,
                          fontWeight: 800, fontSize: 13,
                        }}>{n.count}</span>
                        <span style={{ flex: 1, fontWeight: 600, color: 'var(--text)' }}>{n.label}</span>
                        <ChevronRight size={16} style={{ color: 'var(--muted)' }} />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Today's leads by source */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--info-soft)', color: 'var(--accent-deep)', display: 'grid', placeItems: 'center' }}><Target size={15} /></span>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>Today&apos;s leads by source</h2>
                </div>
                {sources.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: 'var(--muted)', fontSize: 13.5 }}>No leads today yet.</div>
                ) : (
                  <div style={{ display: 'grid', gap: 12 }}>
                    {sources.map((s) => (
                      <div key={s.key} style={{ display: 'flex', alignItems: 'center', gap: 12 }} data-testid="overview-source-row">
                        <div style={{ width: 120, flex: '0 0 120px' }}>
                          <span style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '4px 10px', borderRadius: 99,
                            border: '1px solid var(--border)',
                            background: 'var(--surface-2)',
                            fontSize: 12.5, fontWeight: 700, color: 'var(--text-2)',
                          }}>
                            <span style={{ width: 7, height: 7, borderRadius: 99, background: SOURCE_COLOR[s.key] || '#888' }} />
                            {s.label}
                          </span>
                        </div>
                        <div style={{ flex: 1 }}><Bar pct={(s.n / maxSrc) * 100} color={SOURCE_COLOR[s.key] || '#11a0c2'} /></div>
                        <b style={{ width: 28, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{s.n}</b>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'grid', gap: 18, alignContent: 'start' }}>
              {/* Cross-app connectivity: kgob.com <-> app.kgob.com */}
              <SyncHealthCard sessionToken={sessionToken} />

              {/* Status board widget (P1 — sync health dots) */}
              <StatusBoard onNav={onNav} sessionToken={sessionToken} />

              {/* Weekly leads + sparkline */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{ fontSize: 13, color: 'var(--muted)', fontWeight: 700 }}>Leads this week</div>
                    <div style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-1px', marginTop: 2 }} data-testid="overview-week-total">{today.weekTotal ?? 0}</div>
                    {today.weekDeltaPct != null && (
                      <span style={{
                        marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 4,
                        fontSize: 11.5, fontWeight: 700,
                        color: today.weekDeltaPct > 0 ? 'var(--ok)' : today.weekDeltaPct < 0 ? 'var(--bad)' : 'var(--muted)',
                      }}>
                        {today.weekDeltaPct > 0 && <ArrowUp size={11} />}
                        {today.weekDeltaPct < 0 && <ArrowDown size={11} />}
                        {today.weekDeltaPct > 0 ? '+' : ''}{today.weekDeltaPct}% vs last
                      </span>
                    )}
                  </div>
                  <Spark data={today.sparkLeads || []} w={130} h={56} color="#11a0c2" />
                </div>
              </div>

              {/* Quick actions */}
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                  <span style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--info-soft)', color: 'var(--accent-deep)', display: 'grid', placeItems: 'center' }}><Sparkles size={15} /></span>
                  <h2 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: 'var(--text)' }}>Quick actions</h2>
                </div>
                <div style={{ display: 'grid', gap: 8 }}>
                  <QuickActionBtn data-testid="overview-quickactionbtn-4" Icon={Plus} label="Go to Leads" onClick={() => onNav('leads')} testid="overview-go-leads" />
                  <QuickActionBtn data-testid="overview-quickactionbtn-3" Icon={Sparkles} label="Review AI queue" onClick={() => onNav('gya_queue')} testid="overview-go-aiqueue" />
                  <QuickActionBtn data-testid="overview-quickactionbtn-2" Icon={RefreshCw} label="Check CRM sync" onClick={() => onNav('crmsync')} testid="overview-go-crmsync" />
                  <QuickActionBtn data-testid="overview-quickactionbtn-1" Icon={Download} label="Export current view" onClick={() => toast.success('Export queued — head to Leads to confirm')} testid="overview-quick-export" />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <style>{`
        @media (max-width: 1100px) {
          .lb2-cols-main { grid-template-columns: 1fr !important; }
        }
        /* Skeleton shimmer — soft sweep across surface-2 base */
        .lb2-shimmer {
          background: var(--surface-2);
          background-image: linear-gradient(
            90deg,
            rgba(0,0,0,0) 0,
            rgba(0,0,0,0.04) 40%,
            rgba(0,0,0,0.07) 50%,
            rgba(0,0,0,0.04) 60%,
            rgba(0,0,0,0) 100%
          );
          background-size: 200% 100%;
          background-repeat: no-repeat;
          animation: lb2-shimmer 1.4s linear infinite;
          border: 1px solid var(--border);
        }
        [data-theme="dark"] .lb2-shimmer {
          background-image: linear-gradient(
            90deg,
            rgba(255,255,255,0) 0,
            rgba(255,255,255,0.06) 40%,
            rgba(255,255,255,0.10) 50%,
            rgba(255,255,255,0.06) 60%,
            rgba(255,255,255,0) 100%
          );
        }
        @keyframes lb2-shimmer {
          0%   { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </div>
  );
}

function QuickActionBtn({ Icon, label, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 12px', borderRadius: 'var(--r-sm)',
        border: '1px solid var(--border)', background: 'var(--surface-2)',
        color: 'var(--text)', textAlign: 'left', fontWeight: 600, fontSize: 13.5,
        cursor: 'pointer', fontFamily: 'inherit',
      }}
    >
      <Icon size={16} style={{ color: 'var(--accent-deep)' }} />
      {label}
    </button>
  );
}
